import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import debounce from "lodash.debounce";

const CareerDevelopmentModal = ({ closePopup }) => {
  const [scores, setScores] = useState({});
  const [customPlans, setCustomPlans] = useState({});
  const [maxValues, setMaxValues] = useState({});
  const [areaPlans, setAreaPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [careIds, setCareIds] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  const [activeTab, setActiveTab] = useState("supervisor");
  const [canAccessSupervisorTab, setCanAccessSupervisorTab] = useState(false);


  useEffect(() => {
    const missingValues = [];
    if (!localStorage.getItem("year")) missingValues.push("Year");
    if (!localStorage.getItem("period")) missingValues.push("Period");
    if (activeTab === "supervisor" && !localStorage.getItem("serviceNo")) missingValues.push("Employee");

    if (missingValues.length > 0) {
      const formattedText =
        missingValues.length === 1
          ? missingValues[0]
          : missingValues.length === 2
            ? missingValues.join(" and ")
            : missingValues.slice(0, -1).join(", ") + ", and " + missingValues.slice(-1);

    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const getServiceNo = () => {

    const serviceNo = localStorage.getItem("serviceNo");
    if (serviceNo) {
      return serviceNo;
    }

    return localStorage.getItem("username");
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {

      const serviceNo = getServiceNo();

      const userType = localStorage.getItem("userType") || "EX";
      const requestToken = localStorage.getItem("request_token");
      const year = localStorage.getItem("year");
      const period = localStorage.getItem("period") || "defaultPeriod";

      const mockData = [
        { id: "1", area: "Professional Qualifications", maxScore: 5 },
        { id: "2", area: "Higher Education", maxScore: 5 },
        { id: "3", area: "Continuous Professional Development - CPD", maxScore: 5 },
      ];


      const endpoint = "GetCareerDev";

      const response = await axios.get(`https://esystems.cdl.lk/backend/PerformanceEvaluationNew/Evaluation/${endpoint}`, {
        params: { serviceNo, UserType: userType, year, period },
        headers: { request_token: requestToken },
      });

      setAreaPlans(mockData);
      const initialScores = {};
      const initialMaxValues = {};
      const initialCareIds = {};
      const initialPlans = {};

      mockData.forEach((item) => {
        initialMaxValues[item.area] = item.maxScore;
        initialScores[item.area] = 0;
        initialCareIds[item.area] = "";
        initialPlans[item.area] = item.plan;
      });

      let hasExistingData = false;
      if (response.data.ResultSet && response.data.ResultSet.length > 0) {
        response.data.ResultSet.forEach((item) => {
          const matchingArea = mockData.find((area) => area.id === item.Care_area);
          if (matchingArea) {
            const score = parseInt(item.Care_Score, 10);
            if (score > 0) {
              hasExistingData = true;
              initialScores[matchingArea.area] = score;
              initialCareIds[matchingArea.area] = item.Care_ID;
              initialPlans[matchingArea.area] = item.Care_Plan || matchingArea.plan;
            }
          }
        });
      }

      setIsUpdate(hasExistingData);
      setScores(initialScores);
      setMaxValues(initialMaxValues);
      setCareIds(initialCareIds);
      setCustomPlans(initialPlans);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to load data. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleScoreChange = (area, value) => {
    const numericValue = parseInt(value, 10) || 0;
    const maxValue = maxValues[area];
    const totalScore = Object.values(scores).reduce((acc, score) => acc + score, 0);

    if (numericValue >= 0 && numericValue <= maxValue) {
      if (totalScore - scores[area] + numericValue <= 5) {
        setScores((prevScores) => ({ ...prevScores, [area]: numericValue }));
      } else {
        Swal.fire({ icon: "warning", title: "Invalid Score", text: `Total score cannot exceed 5.` });
      }
    } else {
      Swal.fire({ icon: "warning", title: "Invalid Score", text: `Maximum allowed score for "${area}" is ${maxValue}` });
    }
  };

  const totalScore = Object.values(scores).reduce((acc, score) => acc + score, 0);
  const debouncedHandleScoreChange = useCallback(debounce(handleScoreChange, 300), [scores, maxValues]);

  const handlePlanChange = (area, value) => {
    setCustomPlans((prevPlans) => ({
      ...prevPlans,
      [area]: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);


    const serviceNo = getServiceNo();

    const userType = localStorage.getItem("userType") || "EX";
    const requestToken = localStorage.getItem("request_token");
    const year = localStorage.getItem("year");
    const peroid = localStorage.getItem("period") || "defaultPeriod";

    const requestData = areaPlans.map((item) => ({
      Care_area: item.id,
      Care_Plan: customPlans[item.area] || item.plan,
      Care_Score: parseInt(scores[item.area] || 0, 10),
    }));

    try {

      const endpoint = "SaveCareerDev";

      await axios.post(`https://esystems.cdl.lk/backend/PerformanceEvaluationNew/Evaluation/${endpoint}`, requestData, {
        params: { UserType: userType, serviceNo: serviceNo, year, peroid },
        headers: { "Content-Type": "application/json", request_token: requestToken },
      });

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: `${activeTab === "self" ? "Self" : "Supervisor"} evaluation ${isUpdate ? "updated" : "saved"} successfully!`
      }).then(() => {
        closePopup();
        window.location.reload();
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || `Failed to ${isUpdate ? "update" : "save"} scores. Please try again.`
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-3xl">
          <button className="absolute top-2 right-2 text-gray-600 hover:text-red-600 text-2xl font-bold" onClick={closePopup}>&times;</button>
          <h2 className="text-2xl font-semibold mb-4">Career Development Score</h2>
          <div className="space-y-4">
            <div className="h-4 bg-gray-300 rounded w-3/4 animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-2/4 animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-3xl">
        <button className="absolute top-2 right-2 text-gray-600 hover:text-red-600 text-2xl font-bold" onClick={closePopup}>&times;</button>
        <h2 className="text-2xl font-semibold mb-4">Career Development Score</h2>

        <table className="w-full border-collapse border border-gray-200 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-4 py-2">Area</th>
              <th className="border px-4 py-2">Plan</th>
              <th className="border px-4 py-2">Score</th>
              <th className="border px-4 py-2">Max</th>
            </tr>
          </thead>
          <tbody>
            {areaPlans.map((item) => (
              <tr key={item.id}>
                <td className="border px-4 py-2">{item.area}</td>
                <td className="border px-4 py-2">
                  <input
                    type="text"
                    className="w-full p-2 border"
                    value={customPlans[item.area] || ''}
                    onChange={(e) => handlePlanChange(item.area, e.target.value)}
                  />
                </td>
                <td className="border px-4 py-2 text-center">
                  <input
                    type="number"
                    className="w-24 text-center border"
                    value={scores[item.area] || 0}
                    onChange={(e) => debouncedHandleScoreChange(item.area, e.target.value)}
                    disabled={totalScore >= 5 && scores[item.area] === 0}
                  />
                </td>
                <td className="border px-4 py-2 text-center bg-yellow-200">{maxValues[item.area]}</td>
              </tr>
            ))}
            <tr>
              <td className="border px-4 py-2 font-semibold" colSpan="2" style={{ backgroundColor: "#FDF2E9" }}>Total</td>
              <td className="border px-4 py-2 text-center font-semibold" style={{ backgroundColor: "#FDF2E9" }}>{totalScore}</td>
              <td className="border px-4 py-2 text-center font-semibold bg-yellow-300">
                5
              </td>
            </tr>
          </tbody>
        </table>

        <div className="flex mt-6 justify-end space-x-1">
          <button
            className={`${activeTab === 'self' ? 'bg-blue-500' : 'bg-blue-500'} text-white px-3 py-2 rounded mt-4`}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isUpdate ? "Update" : "Save"}
          </button>
          <button className="bg-red-500 text-white px-3 py-2 rounded mt-4" onClick={closePopup}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default CareerDevelopmentModal;